/**
 * Type-safe error types for backend communication
 *
 * This module provides typed error classes to distinguish between different
 * types of backend failures, ensuring proper error handling throughout the app.
 */

/**
 * Base error class for all backend communication errors
 */
export class BackendError extends Error {
	readonly statusCode: number;
	readonly code: string;
	readonly originalError?: unknown;

	constructor(
		message: string,
		statusCode: number,
		code: string,
		originalError?: unknown,
	) {
		super(message);
		this.name = "BackendError";
		this.statusCode = statusCode;
		this.code = code;
		this.originalError = originalError;
		// Maintains proper stack trace for where our error was thrown (only available on V8)
		if (Error.captureStackTrace) {
			Error.captureStackTrace(this, BackendError);
		}
	}
}

/**
 * Error thrown when backend server is unreachable (connection refused, timeout, etc.)
 */
export class BackendConnectionError extends BackendError {
	readonly url: string;

	constructor(message: string, url: string, originalError?: unknown) {
		super(message, 503, "BACKEND_CONNECTION_ERROR", originalError);
		this.name = "BackendConnectionError";
		this.url = url;
	}
}

/**
 * Error thrown when backend returns an error response (4xx, 5xx)
 */
export interface BackendResponseErrorOptions {
	code?: string;
	message: string;
	responseBody?: string;
	statusCode: number;
	url: string;
}

export class BackendResponseError extends BackendError {
	readonly url: string;
	readonly responseBody?: string;

	constructor(options: BackendResponseErrorOptions) {
		super(
			options.message,
			options.statusCode,
			options.code || "BACKEND_RESPONSE_ERROR",
		);
		this.name = "BackendResponseError";
		this.url = options.url;
		if (options.responseBody !== undefined) {
			this.responseBody = options.responseBody;
		}
	}
}

/**
 * Error thrown when an API call is blocked because the user is not authenticated.
 * This is different from a 401 response - it means the call was never made
 * because we know the user isn't logged in, preventing server-side error spam.
 */
export class AuthenticationBlockedError extends BackendError {
	constructor(message = "API call blocked: user not authenticated") {
		super(message, 401, "AUTH_BLOCKED");
		this.name = "AuthenticationBlockedError";
	}
}

/**
 * Type guard to check if an error is an AuthenticationBlockedError
 */
export function isAuthenticationBlockedError(
	error: unknown,
): error is AuthenticationBlockedError {
	return error instanceof AuthenticationBlockedError;
}
